import React, { useEffect, useState } from "react";
import panelApi from "@/lib/panelApi";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, Phone, Building2, Mail, Clock } from "lucide-react";

/**
 * Password Reset Requests — inbox for admin/operasyon.
 * Users submit their company + email + phone from the login screen; the admin
 * then phones them and resets the password via the Users page. Marking as
 * "Çözüldü" archives the request.
 */
export default function AcentePasswordResetRequests() {
  const [rows, setRows] = useState([]);

  const load = () => {
    panelApi.get("/password-reset-requests")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  };
  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    try {
      await panelApi.patch(`/password-reset-requests/${id}/resolve`);
      toast.success("Talep çözüldü olarak işaretlendi");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Güncellenemedi"); }
  };

  const pending = rows.filter((r) => r.status === "beklemede");
  const done = rows.filter((r) => r.status !== "beklemede");

  return (
    <div className="space-y-6" data-testid="acente-password-resets">
      <header>
        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Şifre Talepleri</div>
        <h1 className="font-serif text-3xl text-brand-navy">Şifre Sıfırlama Talepleri</h1>
        <p className="text-sm text-slate-600 mt-1">Şirketleri arayıp doğruladıktan sonra Kullanıcılar sayfasından şifreyi yenileyin; buradan da <b>Çözüldü</b> olarak işaretleyin.</p>
      </header>

      {/* Pending */}
      <section>
        <h2 className="font-serif text-lg text-brand-navy mb-3 flex items-center gap-2">
          <Clock size={16} className="text-amber-600" /> Bekleyen ({pending.length})
        </h2>
        {pending.length === 0 ? (
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-8 text-center text-slate-500 text-sm">
            Bekleyen talep yok.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {pending.map((r) => (
              <div key={r.id} className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2" data-testid={`pwreset-pending-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-amber-800">Talep {r.created_at?.slice(0, 16).replace("T", " ")}</div>
                    <div className="font-serif text-lg text-brand-navy mt-1 flex items-center gap-2"><Building2 size={16}/> {r.company_name}</div>
                  </div>
                  <button
                    onClick={() => resolve(r.id)}
                    className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1"
                    data-testid={`pwreset-resolve-${r.id}`}
                  >
                    <CheckCircle2 size={13} /> Çözüldü
                  </button>
                </div>
                <div className="text-sm space-y-1 text-slate-700">
                  <div className="flex items-center gap-2"><Mail size={13} className="text-slate-500"/> <a href={`mailto:${r.email}`} className="text-brand-orange hover:underline">{r.email}</a></div>
                  {r.phone && <div className="flex items-center gap-2"><Phone size={13} className="text-slate-500"/> <a href={`tel:${r.phone}`} className="text-brand-orange hover:underline">{r.phone}</a></div>}
                  {r.note && <div className="text-xs text-slate-600 bg-white/70 rounded-md p-2 mt-1">{r.note}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Done */}
      {done.length > 0 && (
        <section>
          <h2 className="font-serif text-lg text-brand-navy mb-3 flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" /> Çözülmüş ({done.length})
          </h2>
          <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-canvas-stone text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left py-2.5 px-4">Tarih</th>
                  <th className="text-left py-2.5 px-4">Şirket</th>
                  <th className="text-left py-2.5 px-4">E-posta</th>
                  <th className="text-left py-2.5 px-4">Telefon</th>
                </tr>
              </thead>
              <tbody>
                {done.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 text-slate-600" data-testid={`pwreset-done-${r.id}`}>
                    <td className="py-2.5 px-4 font-mono text-xs">{r.resolved_at?.slice(0, 10)}</td>
                    <td className="py-2.5 px-4">{r.company_name}</td>
                    <td className="py-2.5 px-4">{r.email}</td>
                    <td className="py-2.5 px-4">{r.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
